import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@keobook/ui';
import { api } from '../lib/api';
import { useMoneyFormatter } from '../lib/useCurrency';

/**
 * QuickBooks-style drill-down. Click any number on P&L or BS, get a modal
 * listing the journal lines that composed it — backed by GET
 * /v1/reports/gl-detail.
 *
 * Two flavors of target:
 *   - SINGLE account ({ accountId }): row was "1000 Cash · Operating" →
 *     show every line on that account with a running balance.
 *   - MULTI account ({ accountIds: [...] }): row was a subtotal or grand
 *     total ("Total revenue", "Net income", "Total assets") → show every
 *     line that hit any of those accounts. Each row shows which account
 *     it belongs to; running balance is suppressed (meaningless across
 *     unrelated accounts).
 *
 * Modal is a portal at document.body so it sits above the Reports page's
 * nested z-index. ESC + overlay click both close.
 */
export type DrillTarget = {
  /** "1000 Cash · Operating" or "Total revenue" — header label. */
  label: string;
  /** Inclusive period start (YYYY-MM-DD) */
  from: string;
  /** Inclusive period end (YYYY-MM-DD) */
  to: string;
} & ({ accountId: string } | { accountIds: string[] });

type DetailRow = {
  entryId: string;
  postedAt: string;
  description: string;
  memo: string | null;
  accountId: string;
  accountCode: string;
  accountName: string;
  debitCents: number;
  creditCents: number;
  balanceCents: number | null;
};

type DetailState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | {
      phase: 'ready';
      openingCents: number;
      closingCents: number;
      lines: DetailRow[];
    };

export function DrillDownPanel({ target, onClose }: { target: DrillTarget; onClose: () => void }) {
  const fmt = useMoneyFormatter();
  const [state, setState] = useState<DetailState>({ phase: 'loading' });

  // Whether we're in multi-account mode — controls per-line account
  // column visibility + suppresses running balance.
  const isMulti = 'accountIds' in target;

  useEffect(() => {
    let alive = true;
    setState({ phase: 'loading' });
    const req = isMulti
      ? api.getGlDetail({ accountIds: target.accountIds, from: target.from, to: target.to })
      : api.getGlDetail({ accountId: target.accountId, from: target.from, to: target.to });
    req
      .then((r) => {
        if (!alive) return;
        setState({
          phase: 'ready',
          openingCents: r.openingBalanceCents,
          closingCents: r.closingBalanceCents,
          lines: r.lines,
        });
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setState({
          phase: 'error',
          message: err instanceof Error ? err.message : 'Could not load detail.',
        });
      });
    return () => {
      alive = false;
    };
    // Spread the dependency surface so a target swap (single → multi or vice
    // versa) re-fetches. We stringify the ids list for ref stability.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMulti,
    isMulti ? target.accountIds.join(',') : null,
    !isMulti ? target.accountId : null,
    target.from,
    target.to,
  ]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Detail for ${target.label}`}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        zIndex: 1100,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '4rem 1.5rem 1.5rem',
        overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 900,
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
          padding: '20px 22px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 12,
            marginBottom: 12,
          }}
        >
          <div>
            <div style={{ fontWeight: 600, fontSize: 18, color: 'var(--kb-gray-900)' }}>
              {target.label}
            </div>
            <div style={{ fontSize: 13, color: 'var(--kb-gray-400)', marginTop: 2 }}>
              {target.from} → {target.to}
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close detail">
            ×
          </Button>
        </div>

        {state.phase === 'loading' && (
          <p className="kb-muted" style={{ padding: '1rem 0' }}>
            Loading lines…
          </p>
        )}
        {state.phase === 'error' && (
          <div role="alert" className="kb-form-error">
            {state.message}
          </div>
        )}
        {state.phase === 'ready' && (
          <>
            {state.lines.length === 0 ? (
              <p className="kb-muted" style={{ padding: '1rem 0' }}>
                No posted lines in this period for this account.
              </p>
            ) : (
              <table className="kb-report" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Date</th>
                    {isMulti && <th style={{ textAlign: 'left' }}>Account</th>}
                    <th style={{ textAlign: 'left' }}>Description</th>
                    <th className="kb-report__amt">Debit</th>
                    <th className="kb-report__amt">Credit</th>
                    {!isMulti && <th className="kb-report__amt">Balance</th>}
                  </tr>
                </thead>
                <tbody>
                  {/* Header subtotal — opening balance only meaningful for
                      single-account drills. For multi-account drills the
                      aggregate opening is misleading (sum of unrelated
                      pre-period balances), so skip the row entirely. */}
                  {!isMulti && (
                    <tr className="kb-report__subtotal">
                      <td colSpan={4}>Opening balance</td>
                      <td className="kb-report__amt">{fmt(state.openingCents)}</td>
                    </tr>
                  )}
                  {state.lines.map((l, i) => (
                    <tr key={`${l.entryId}-${l.accountId}-${i}`}>
                      <td>{new Date(l.postedAt).toLocaleDateString()}</td>
                      {isMulti && (
                        <td>
                          <span className="kb-report__code">{l.accountCode}</span> {l.accountName}
                        </td>
                      )}
                      <td>
                        {l.description}
                        {l.memo ? <span className="kb-muted"> — {l.memo}</span> : null}
                      </td>
                      <td className="kb-report__amt">
                        {l.debitCents ? fmt(l.debitCents) : ''}
                      </td>
                      <td className="kb-report__amt">
                        {l.creditCents ? fmt(l.creditCents) : ''}
                      </td>
                      {!isMulti && (
                        <td className="kb-report__amt">
                          {l.balanceCents !== null ? fmt(l.balanceCents) : ''}
                        </td>
                      )}
                    </tr>
                  ))}
                  {/* Footer: single mode shows the running-balance closing.
                      Multi mode shows the net change (sum of debits minus
                      credits over the period) — the figure that matches the
                      P&L / BS subtotal the user clicked. Both modes have 5
                      columns; the label spans 4 and the value sits in the
                      rightmost amount column. */}
                  <tr className="kb-report__total">
                    <td colSpan={4}>{isMulti ? 'Net change' : 'Closing balance'}</td>
                    <td className="kb-report__amt">
                      {isMulti
                        ? fmt(state.closingCents - state.openingCents)
                        : fmt(state.closingCents)}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
